import { useEffect } from "react";
import { Global } from "@emotion/react";
import userflow from "userflow.js";

import { USERFLOW_TOP_LAYER, useVariableValue } from "lib/featureFlags";
import { PORTAL_CONTAINER_ID } from "./TopLayerWrapper";

const Userflow = () => {
  const userflowTopLayer = useVariableValue(USERFLOW_TOP_LAYER, false);

  useEffect(() => {
    // Hack to workaround Userflow not allowing configuration of where the userflow-ui element is placed in the DOM.
    // Therefore, we watch for it to be added to the DOM and move it to our top-layer container, which is on the top-layer of the app.

    // I have contacted them to ask them to allow configuration of where the userflow-ui element is placed in the DOM
    // but until then, we have to use this workaround IF we want userflow to work with our modals and drawers

    // My original approach was add another userflow-ui elemeent above it in the DOM and due to their usage of document.getElementById,
    // their code would use our element instead of the one they create. However, this had issues (can't remember why), which lead me to this approach.
    if (!userflowTopLayer) {
      return;
    }

    const observer = new MutationObserver((mutations) => {
      const nodes = mutations.flatMap(({ addedNodes }) => Array.from(addedNodes));
      for (const node of nodes) {
        if (node instanceof Element && node.id === "userflow-ui") {
          const topLayerContainer = document.getElementById(PORTAL_CONTAINER_ID);
          if (!topLayerContainer) {
            console.warn(
              "Userflow UI element found but no top-layer container exists! Skipping moving it - it may not always be visible.",
            );
            return;
          }
          topLayerContainer.appendChild(node);
        }
      }
    });
    observer.observe(document.body, {
      subtree: false,
      childList: true,
    });

    return () => {
      observer.disconnect();

      // If we have moved the userflow-ui element, we need to move it back to the body
      // to prevent an error in Userflow's code when it tries to unmount itself
      // This should hopefully run first since this useEffect cleanup should run before it starts unmounting
      const userflowUI = document.getElementById("userflow-ui");
      if (userflowUI && userflowUI.parentElement !== document.body) {
        document.body.appendChild(userflowUI);
      }
    };
  }, [userflowTopLayer]);

  useEffect(() => {
    const loadUserflow = async () => {
      const currentUser = window.currentUser;
      const USERFLOW_TOKEN = window.userflowToken;

      if (currentUser && USERFLOW_TOKEN) {
        try {
          await userflow.load();

          userflow.init(USERFLOW_TOKEN);
          await userflow.identify(
            currentUser?.guid,
            {
              name: currentUser?.first_name + " " + currentUser?.last_name,
              first_name: currentUser?.first_name,
              last_name: currentUser?.last_name,
              email: currentUser?.email,
              data_types: currentUser?.active_data_types,
              signed_up_at: currentUser?.created_at,
              device_type: window.innerWidth > 992 ? "Desktop" : "Mobile",
            },
            {
              signature: window.userflowUserSignature,
            },
          );
          await userflow.group(
            currentUser?.company.guid,
            {
              name: currentUser?.company.name,
              payment_type: currentUser?.company.payment_type || "Not set",
              signed_up_at: currentUser?.company.created_at,
            },
            { signature: window.userflowGroupSignature },
          );
        } catch (e) {
          console.warn("Error loading userflow", e);
        }
      }
    };

    void loadUserflow();
  }, []);

  return (
    <Global
      styles={{
        "#userflow-ui": {
          "@media print": {
            display: "none",
          },
        },
      }}
    />
  );
};

export default Userflow;
